import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { useMemo } from 'react';

// Make sure your type definitions are robust
interface AIInsight { 
  id: string; 
  generated_by_agent: string; 
  insight_text: string; 
  created_at: string; 
}

interface Deal { 
  id: string; 
  name: string; 
  insights?: AIInsight[]; 
}

interface Contact { 
  id: string; 
  email: string; 
  insights?: AIInsight[]; 
}

interface AIInsightsFeedProps {
    deals: Deal[];
    contacts: Contact[];
}

// Helper to add context to each insight
const getInsightsWithContext = (deals: Deal[], contacts: Contact[]) => {
    let allInsights: Array<AIInsight & { context: string }> = [];
    
    for (const deal of deals) {
        deal.insights?.forEach(insight => 
            allInsights.push({ ...insight, context: `Deal: ${deal.name}`})
        );
    }
    
    for (const contact of contacts) {
        contact.insights?.forEach(insight => 
            allInsights.push({ ...insight, context: `Contact: ${contact.email}`})
        );
    }
    
    return allInsights.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
};

const getAgentColor = (agent: string): "primary" | "secondary" | "success" | "warning" => {
  switch (agent) {
    case "CEO Agent": return "primary";
    case "CRO Agent": return "success";
    case "CSO Agent": return "warning";
    default: return "secondary";
  }
}

export default function AIInsightsFeed({ deals, contacts }: AIInsightsFeedProps) {
    const sortedInsights = useMemo(() => getInsightsWithContext(deals, contacts), [deals, contacts]);

    if (sortedInsights.length === 0) {
        return <Typography>No AI insights available for this account yet.</Typography>;
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>C-Suite AI Advisory</Typography>
            {sortedInsights.map(insight => (
                <Card key={insight.id} sx={{ mb: 2 }}>
                    <CardContent>
                        <Chip 
                            label={insight.generated_by_agent}
                            color={getAgentColor(insight.generated_by_agent)} 
                            size="small" 
                            sx={{ mb: 1 }}
                        />
                        <Typography variant="body2">{insight.insight_text}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Regarding: {insight.context}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
} 